/**
 * ConsentBanner — RGPD cookie consent banner shown on first visit.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Shield,
  X,
  Settings,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useConsentStore } from '@/stores/useConsentStore'
import type { ConsentPreferences } from '@/stores/useConsentStore'

export function ConsentBanner() {
  const { hasResponded, showBanner, acceptAll, refuseAll, savePreferences, dismissBanner } = useConsentStore()
  const [showDetails, setShowDetails] = useState(false)
  const [prefs, setPrefs] = useState<ConsentPreferences>({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
    ai_personalization: false,
  })
  const navigate = useNavigate()

  // A modal ([role=dialog]) must NEVER be obscured by the consent banner. Radix
  // dialog overlay/content sit at z-50; the banner sits above normal page content
  // but BELOW any open dialog, and — because a tall create modal's footer can
  // still visually overlap the fixed banner — we fully hide the banner (and drop
  // its pointer events) for as long as a dialog is open. The banner reappears the
  // instant the dialog closes, so accept/reject stay reachable afterwards.
  const [dialogOpen, setDialogOpen] = useState(false)
  useEffect(() => {
    if (typeof document === 'undefined') return
    const recompute = () => {
      // Radix marks the open content with data-state="open"; a plain [role=dialog]
      // (non-Radix modals) also counts. Either presence hides the banner.
      const anyDialog =
        document.querySelector('[role="dialog"][data-state="open"]') ||
        document.querySelector('[role="dialog"]') ||
        document.querySelector('[role="alertdialog"]')
      setDialogOpen(Boolean(anyDialog))
    }
    recompute()
    const observer = new MutationObserver(recompute)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'data-state'] })
    return () => observer.disconnect()
  }, [])

  // Don't show if already responded, dismissed, or while a modal is open.
  if (hasResponded || !showBanner || dialogOpen) return null

  const handleSave = () => {
    savePreferences(prefs)
  }

  return (
    <div role="region" aria-label="Consentement aux cookies" className="fixed bottom-0 left-0 right-0 z-40 p-4 pointer-events-none">
      <div className="mx-auto max-w-2xl pointer-events-auto">
        <div className="rounded-xl border bg-card shadow-lg">
          {/* Header */}
          <div className="flex items-start gap-3 p-4 pb-2">
            <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Respect de votre vie privee</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Nous utilisons des cookies pour ameliorer votre experience, analyser le trafic et personnaliser le contenu.
                Vous pouvez gerer vos preferences a tout moment.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={dismissBanner}
              aria-label="Fermer la bannière de consentement"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Details panel */}
          {showDetails && (
            <div className="px-4 py-3 border-t space-y-3">
              <ConsentToggle
                label="Necessaires"
                description="Requis pour le fonctionnement du site"
                checked={true}
                disabled={true}
              />
              <ConsentToggle
                label="Fonctionnels"
                description="Preferences, langue, theme"
                checked={prefs.functional}
                onChange={(v) => setPrefs({ ...prefs, functional: v })}
              />
              <ConsentToggle
                label="Analytiques"
                description="Statistiques d'utilisation anonymisees"
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
              />
              <ConsentToggle
                label="Marketing"
                description="Contenu personnalise et publicites"
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
              />
              <ConsentToggle
                label="Personnalisation IA"
                description="Suggestions et recommandations IA"
                checked={prefs.ai_personalization}
                onChange={(v) => setPrefs({ ...prefs, ai_personalization: v })}
              />
            </div>
          )}

          {/* Actions — wrap on phones so all four buttons stay reachable */}
          <div className="flex flex-wrap items-center gap-2 p-4 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex-1 sm:flex-none min-w-[8rem] sm:min-w-0"
              onClick={() => setShowDetails(!showDetails)}
            >
              <Settings className="h-3.5 w-3.5 mr-1" />
              {showDetails ? 'Masquer' : 'Personnaliser'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex-1 sm:flex-none min-w-[8rem] sm:min-w-0"
              onClick={() => navigate('/legal/privacy')}
            >
              Politique
            </Button>
            <div className="hidden sm:block sm:flex-1" />
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex-1 sm:flex-none min-w-[8rem] sm:min-w-0"
              onClick={showDetails ? handleSave : refuseAll}
            >
              {showDetails ? 'Sauvegarder' : 'Refuser'}
            </Button>
            <Button
              size="sm"
              className="text-xs flex-1 sm:flex-none min-w-[8rem] sm:min-w-0"
              onClick={acceptAll}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Tout accepter
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ConsentToggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange?: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </div>
  )
}
